import { useEvents } from '@/react/kubernetes/queries/useEvents';
import {
  filterRelatedEvents,
  useHelmEventsTableState,
} from '@/react/kubernetes/helm/release/ReleaseDetails/HelmEventsDatatable';

import { useAddonReleaseScope } from './queries/useAddonReleaseScope';

/**
 * useAddonEventWarningsCount returns how many of the add-on's events are
 * warnings, for the Events tab badge. It mirrors AddonEventsTab's data sources
 * and filtering so it counts the exact rows the table shows, and shares their
 * query keys so no extra requests are made: release-scoped events when the
 * release exists, otherwise all events in the namespace.
 */
export function useAddonEventWarningsCount(id: string) {
  const { scope, query, environmentId, namespace } = useAddonReleaseScope(id, {
    showResources: true,
  });
  const { autoRefreshRateMS } = useHelmEventsTableState();

  const hasRelease = scope === 'release';
  const { data: warningCount } = useEvents(environmentId ?? 0, {
    namespace,
    queryOptions: {
      // Count once there's something to read: the release (filtered to its
      // resources) or, if it's absent, the whole namespace.
      enabled: scope === 'release' || scope === 'namespaceOnly',
      autoRefreshRate: autoRefreshRateMS,
      select: (data) => {
        const events = hasRelease
          ? filterRelatedEvents(data, query.data?.info?.resources ?? [])
          : data;
        return events.filter((e) => e.type === 'Warning').length;
      },
    },
  });

  return warningCount ?? 0;
}
